/**
 * SLAM Design System — Inline Style Constants (Kinetic Ledger instance)
 *
 * Shorthand `C` object for use in inline React styles.
 * Adapted from Stitch prototype namedColors for the Kinetic Ledger design system.
 *
 * Usage:
 *   import { C } from '../design-system/primitives';
 *   <div style={{ background: C.bg, color: C.text, border: `1px solid ${C.border}` }}>
 */

import { getBrandAccent } from '../../../lib/branding/instanceTheme';

const accent = getBrandAccent();

export const C = {
  // Backgrounds (Kinetic Ledger surface layers)
  bg: '#10141a',          // surface (Layer 0 — base)
  s1: '#181c22',          // surface_container_low (Layer 1)
  s2: '#1c2026',          // surface_container (Layer 2 — cards)
  s3: '#262a31',          // surface_container_high (Layer 3 — active)
  s4: '#31353c',          // surface_container_highest
  s0: '#0a0e14',          // surface_container_lowest (recessed)

  // Surface semantic aliases
  surfaceBright: '#353940',
  surfaceDim: '#10141a',

  // Borders — Ghost Border rule: outline_variant at 15% opacity
  border: 'rgba(77, 71, 50, 0.15)',   // outline_variant ghost
  borderLight: '#4d4732',              // outline_variant full
  borderSubtle: 'rgba(77, 71, 50, 0.08)',

  // Gold accent system (primary)
  gold: accent.gold,                   // #FED603 — primary_container
  goldMuted: accent.goldMuted,         // #e8c200 — primary_fixed_dim
  goldDim: accent.goldDim,             // #4A3900
  goldLight: accent.goldLight,         // #ffe16d — primary_fixed
  g15: accent.g15,
  g08: accent.g08,

  // Tertiary accent (cyan)
  cyan: '#0c92f1',                     // tertiary_container
  cyanMuted: '#0a7ac8',               // tertiary_fixed_dim
  cyanDim: '#006a70',                 // on_tertiary_container

  // Text (on_background hierarchy)
  text: '#dfe2eb',        // on_background — primary text
  t2: '#999077',          // outline — secondary text
  t3: '#9ea6b4',          // muted tertiary (readable on dark surfaces)
  t4: '#858d9c',          // quaternary / table & section headers

  // Status
  success: '#10B981',
  warning: '#f59e0b',
  error: '#ffb4ab',       // Stitch error
  errorDark: '#93000a',   // error_container
  info: '#0a7ac8',        // tertiary_fixed_dim

  // Medical status badges
  statusOut: '#ef4444',
  statusLimited: '#f59e0b',
  statusFullGo: '#10B981',
  statusGTD: '#6b7280',
};

/**
 * Tier classification function.
 * Returns { color, label } based on a 0-100 score.
 */
export const tier = (v) => ({
  color: v >= 90 ? '#13294B' : v >= 70 ? '#00CC66' : v >= 40 ? C.gold : '#FF4444',
  label: v >= 90 ? 'GEN' : v >= 70 ? 'ELITE' : v >= 40 ? 'SOLID' : 'DEV',
});
